import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ComponentRef } from '@angular/core';
import { distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';
import { collectionData, collection, Firestore, CollectionReference } from '@angular/fire/firestore';
import { firstValueFrom, Observable, Subject, Subscription } from 'rxjs';
import { AuthStoreDispatcher } from 'src/app/core/state/auth/auth.dispatcher';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { App } from '@capacitor/app';
import { isElectron, isNativePlatform } from 'src/app/shared/utils/platform.utils';
import { AppUpdate, AppUpdateAvailability } from '@capawesome/capacitor-app-update';
import { ProfileStateDispatcher } from 'src/app/core/state/profile/profile.dispatcher';
import { SopoAlertComponent } from 'src/app/sopo-popup/components/sopo-alert/sopo-alert.component';
import { SopoDialogRef } from 'src/app/sopo-popup/interfaces/dialog';
import { SopoPopupService } from 'src/app/sopo-popup/service/sopo-popup.service';
import { ShowUpdateComponent } from '../show-update/show-update.component';
import AppDetailsElectron from 'src/app/plugins/app-details.plugin';

interface IMaintenance {
    activate: boolean;
    header: string;
    message: string;
}
interface AccountDocument {
    id: string;
    inMaintenance: IMaintenance;
    preMaintenance: IMaintenance;
    isForceLogout: boolean;
    latestVersion: string;
    latestDesktopVersion: string;
    sopoBalance: number;
    testUsers: string[];
}

interface IAlerts {
    maintenanceAlert: ComponentRef<SopoAlertComponent> | 'progress' | null;
    preMaintenanceAlert: ComponentRef<SopoAlertComponent> | 'progress' | null;
}

@Component({
    selector: 'show-maintenance',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './show-maintenance.component.html',
    styleUrls: ['./show-maintenance.component.scss'],
    standalone: false
})
export class ShowMaintenance implements OnInit, OnDestroy {
    ngUnsubscribe = new Subject<void>();
    accountSubscriptions: Subscription = new Subscription();
    alerts: IAlerts = {
        maintenanceAlert:  null,
        preMaintenanceAlert: null,
    }
    isStaging = false;
    appInfo: { version: string };
    updatePopUpIsOpen = false;
    maintenanceAlertRef: ComponentRef<SopoAlertComponent>;
    preMaintenanceAlertRef: ComponentRef<SopoAlertComponent>;
    updateDialogRef: SopoDialogRef;

    constructor(
        public firestore: Firestore,
        private authService: AuthStoreDispatcher,
        private router: Router,
        private profileService: ProfileStateDispatcher,
        private sopoPopupService: SopoPopupService,
    ) {}

    ngOnInit() {
        this.init();
    }

    async init(){
        this.isStaging = environment.staging;
        this.initSubscriptions();
    }

    async getAppInfo() {
        if (isElectron()) this.appInfo = await AppDetailsElectron.getAppInfo();
        else this.appInfo = await App.getInfo();
    }

    initSubscriptions(){
        const collectionQuery = collection(this.firestore, `account`) as CollectionReference<AccountDocument>;
        const account$ = collectionData<AccountDocument>(collectionQuery)
            .pipe(
                distinctUntilChanged(),
                takeUntil(this.ngUnsubscribe),
                filter(accounts => accounts.length > 0),
                map(accounts => accounts[0])
            );

        const forceLogoutSubscription = account$.pipe(
            map(accoutnDoc => accoutnDoc.isForceLogout),
            distinctUntilChanged(),
            filter(isForceLogout => isForceLogout)
        ).subscribe(() => {
            this.logoutAllUsers();
        });
        this.accountSubscriptions.add(forceLogoutSubscription);

        const maitenanceSubscription = account$.pipe(
            map(accountDoc => {
                return {...accountDoc.inMaintenance, testUsers: accountDoc.testUsers}
            })
        ).subscribe((maintenance) => {
            if (maintenance.activate) this.showMaintenanceAlert(maintenance.header, maintenance.message, maintenance.testUsers);
            else if (this.alerts.maintenanceAlert) this.dismissAlert('maintenanceAlert');
        });
        this.accountSubscriptions.add(maitenanceSubscription);

        const preMaintenanceSubscription = account$.pipe(
            map(accoutnDoc => accoutnDoc.preMaintenance),
        ).subscribe(preMaintenance => {
            if (preMaintenance.activate) this.showPreMaintenanceAlert(
                preMaintenance.header,
                preMaintenance.message
            );
            else if (this.alerts.preMaintenanceAlert) this.dismissAlert('preMaintenanceAlert');
        });
        this.accountSubscriptions.add(preMaintenanceSubscription);

        if (isNativePlatform()) this.initMobileSubscriptions(account$);
    }

    async initMobileSubscriptions(account$: Observable<AccountDocument>){
        await this.getAppInfo();
        const forceVersionSubscription = account$.pipe(
            map(accoutnDoc => {
                if (isElectron()) return accoutnDoc.latestDesktopVersion;
                else return accoutnDoc.latestVersion;
            }),
            distinctUntilChanged(),
        ).subscribe(async forcedVersion => {
            if (this.isNewVersion(this.appInfo.version, forcedVersion)) this.forceUpdate(forcedVersion);
            else {
                if (this.updatePopUpIsOpen) {
                    await this.updateDialogRef.close(false);
                    this.updatePopUpIsOpen = false;
                }
                this.checkUpdateFromStore();
            }
        });
        this.accountSubscriptions.add(forceVersionSubscription);
    }

    getMyEmail(){
        return firstValueFrom(this.profileService.getProfileState().pipe(
            map(profileState => {
                if (profileState.profileData !== null) return profileState.profileData.email;
                else return '';
            }),
        ));
    }

    isNewVersion(oldVer: string, newVer: string): boolean {
        const oldParts = oldVer.split('.');
        const newParts = newVer.split('.');
        for (let i = 0; i < newParts.length; i++) {
            // tslint:disable-next-line: radix
            const a = parseInt(newParts[i]) || 0;
            // tslint:disable-next-line: radix
            const b = parseInt(oldParts[i]) || 0;
            if (a > b) {
                return true;
            }
            if (a < b) {
                return false;
            }
        }
        return false;
    }

    logoutAllUsers() {
        this.authService.logout();
    }

    async forceUpdate(newVersion) {
        if (this.router.url.includes('/lobby') || this.router.url.includes('/login')) {
            this.showUpdatePopup(newVersion, true);
        } else {
            this.showUpdatePopup(newVersion, false);
        }
    }

    async showUpdatePopup(newVersion: string, isForced: boolean) {
        if(this.updatePopUpIsOpen) return;
        this.updatePopUpIsOpen = true;

        this.updateDialogRef = this.sopoPopupService.openDialog('app', ShowUpdateComponent, {
            backdropDismiss: false,
            closeButton: false,
            data: {
                isForced,
                version: newVersion,
            },
            zIndex: 2002,
        });

        const result: boolean = await firstValueFrom(this.updateDialogRef.afterClosed);
        this.updatePopUpIsOpen = false;
    }

    async showPreMaintenanceAlert(header: string, message: string) {
        if (this.alerts.preMaintenanceAlert === 'progress') return;
        if (this.alerts.preMaintenanceAlert !== null) await this.dismissAlert('preMaintenanceAlert');
        this.alerts.preMaintenanceAlert = 'progress';

        this.alerts.preMaintenanceAlert = await this.sopoPopupService.showAlert('app',{
            header: header,
            message: message,
            buttonText: 'OK',
            preventAutoDismiss: true,
        });

        if (this.alerts.maintenanceAlert) this.dismissAlert('preMaintenanceAlert');
    }

    async showMaintenanceAlert(header: string, message: string, testUsers: string[]) {
        if (this.alerts.maintenanceAlert === 'progress') return;
        if (this.alerts.maintenanceAlert !== null) await this.dismissAlert('maintenanceAlert');
        this.alerts.maintenanceAlert = 'progress';
        const myEmail = await this.getMyEmail();
        if (testUsers.indexOf(myEmail) < 0) {
            this.alerts.maintenanceAlert = this.sopoPopupService.showAlert('app', {
                header: header,
                message: message,
                backdropDismiss: false,
                preventAutoDismiss: true,
            });
        }
        if (this.alerts.preMaintenanceAlert) {
            this.dismissAlert('preMaintenanceAlert');
        }
    }

    async checkUpdateFromStore() {
        if (isElectron()) return;
        const updateInfo = await AppUpdate.getAppUpdateInfo();
        if (updateInfo.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE) {
            this.showUpdatePopup('', false);
        }
    }

    async dismissAlert(alertKey: keyof IAlerts){
        if (this.alerts[alertKey] !== null || this.alerts[alertKey] !== 'progress') await (this.alerts[alertKey] as ComponentRef<SopoAlertComponent>).instance.dismiss();
        this.alerts[alertKey] = null;
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
        if (this.accountSubscriptions) {
            this.accountSubscriptions.unsubscribe();
        }
    }
}
